import type { UseFormReturnType } from '@mantine/form';
import type {
  SessionFrequency,
  PreferredDay,
  PreferredTime,
  Partner,
  Program,
  PartnerContactWithCohortCount,
  ModalityType,
  WorkshopPattern,
  ParticipantAssignment,
  DayOfWeek,
  MeetingPlatform,
  CoachingSubType,
} from '../../../types';
import type { InternalUser } from '../../../services/api/users';
import type { JobTitleGroup } from '../../../hooks/useJobTitles';
import type { CheckinSettingsForm } from '../components/CheckinSettingsFields';

// ========================
// FORM VALUE TYPES
// ========================

// Local interface for form constraints (uses Date objects)
export interface FormDateConstraint {
  start_date: Date | null;
  end_date: Date | null;
  label: string;
}

// Workshop form input
export interface FormWorkshopInput {
  title: string;
  sequence_number?: number;
  participant_assignment: ParticipantAssignment;
  facilitator_id: string | null;
  attendance_tracking_enabled: boolean;
  sessionDurationMinutes: number;
}

// Per-modality session configuration
export interface GroupCoachingConfig {
  sessionCount: number;
  frequency: SessionFrequency;
  sessionDurationMinutes: number;
  // MED-848 — required for group_coaching (AC2/AC3). null until the CM picks one.
  coachingSubType: CoachingSubType | null;
  // MED-948 (E1.10a) — upstream gate for aiFeaturesEnabled. Defaults false;
  // when off, the AI toggle is hidden and aiFeaturesEnabled is forced false
  // (AC1/AC2/AC4). Persisted as modalities.live_session_delivery_enabled.
  liveSessionDeliveryEnabled: boolean;
  // MED-848 — defaults false; CM explicitly opts in (AC4/AC5). Only meaningful
  // when liveSessionDeliveryEnabled is true (MED-948).
  aiFeaturesEnabled: boolean;
  // MED-1069 (E1.10c) — Commitment Tracking. Defaults false (AC7), independent
  // of liveSessionDeliveryEnabled. Persisted as
  // modalities.commitment_tracking_enabled on the group_coaching modality.
  commitmentTrackingEnabled: boolean;
}

import type { ConfigurableOneOnOneType } from '../../../types';

export interface OneOnOneSessionConfigForm {
  sessionNumber: number;
  durationMinutes: number;
  oneOnOneType: ConfigurableOneOnOneType;
}

export interface OneOnOneConfig {
  sessionCount: number;
  frequency: SessionFrequency;
  sessionDurationMinutes: number;
  sessionConfigs: OneOnOneSessionConfigForm[];
}

export interface WorkshopConfig {
  pattern: WorkshopPattern;
  workshops: FormWorkshopInput[];
  // MED-848 — defaults false; CM explicitly opts in (AC4/AC5/AC11).
  aiFeaturesEnabled: boolean;
}

// Session dates organized by modality
export interface ModalitySessionDates {
  group_coaching: (Date | null)[];
  one_on_one: (Date | null)[];
  workshop: (Date | null)[];
}

// Time slot entry for group coaching
export interface FormTimeSlot {
  id: string; // local ID for form management
  day_of_week: DayOfWeek;
  time: string; // HH:MM format
  timezone: string;
}

// Cohort-level dates (not session dates)
export interface CohortDates {
  coachImmersion: Date | null;
  participantOrientation: Date | null;
  coachHuddles: (Date | null)[];
  cohortWrapUp: Date | null;
}

export interface FormValues {
  // Step 1: Partner & Program
  partnerId: string;
  primaryContactIds: string[];
  programId: string;
  newProgramName: string;

  // Step 2: Cohort Details
  name: string;
  shortName: string;
  code: string;
  defaultMeetingPlatform: MeetingPlatform | '';

  // Step 3: Modalities
  selectedModalities: ModalityType[];

  // Step 4: Session Configuration (per modality)
  groupCoachingConfig: GroupCoachingConfig;
  oneOnOneConfig: OneOnOneConfig;
  workshopConfig: WorkshopConfig;
  preferredDays: PreferredDay[];
  preferredTimes: PreferredTime[];
  // MED-1350: intro and midpoint check-ins
  checkins: CheckinSettingsForm;

  // Step 5: Dates
  dateConstraints: FormDateConstraint[];
  cohortDates: CohortDates;
  sessionDates: ModalitySessionDates;
  timeSlots: FormTimeSlot[];

  // Step 6: Review & Team Assignments (keyed by job_title_id)
  teamAssignments: Record<string, string>;
}

// ========================
// DEFAULT VALUES
// ========================

export const DEFAULT_GROUP_COACHING_CONFIG: GroupCoachingConfig = {
  sessionCount: 6,
  frequency: 'biweekly',
  sessionDurationMinutes: 90,
  // MED-848 — null forces an explicit pick before the wizard advances (AC3).
  coachingSubType: null,
  // MED-948 (E1.10a) — both gates default off (AC2).
  liveSessionDeliveryEnabled: false,
  aiFeaturesEnabled: false,
  // MED-1069 (E1.10c) — Commitment Tracking defaults off (AC7).
  commitmentTrackingEnabled: false,
};

export const DEFAULT_ONE_ON_ONE_CONFIG: OneOnOneConfig = {
  sessionCount: 4,
  frequency: 'biweekly',
  sessionDurationMinutes: 60,
  sessionConfigs: [],
};

export const DEFAULT_WORKSHOP_CONFIG: WorkshopConfig = {
  pattern: 'parallel',
  workshops: [],
  aiFeaturesEnabled: false,
};

export const DEFAULT_WORKSHOP_INPUT: FormWorkshopInput = {
  title: '',
  participant_assignment: 'all_participants',
  facilitator_id: null,
  attendance_tracking_enabled: true,
  sessionDurationMinutes: 60,
};

export const DEFAULT_COHORT_DATES: CohortDates = {
  coachImmersion: null,
  participantOrientation: null,
  coachHuddles: [],
  cohortWrapUp: null,
};

// ========================
// STEP PROPS
// ========================

// Props shared across step components
export interface StepProps {
  form: UseFormReturnType<FormValues>;
}

// Step 1: Partner & Program
export interface PartnerProgramStepProps extends StepProps {
  partners: Partner[];
  programs: Program[];
  partnerContacts: PartnerContactWithCohortCount[];
  loadingPartners: boolean;
  loadingPrograms: boolean;
  loadingContacts: boolean;
  onNavigateToPartners: () => void;
}

// Step 2: Cohort Details
export interface CohortDetailsStepProps extends StepProps {
  checkingCode: boolean;
  codeAvailable: boolean | null;
  onCheckCode: (code: string) => void;
  checkingName: boolean;
  nameAvailable: boolean | null;
  onCheckName: (name: string) => void;
  checkingShortName: boolean;
  shortNameAvailable: boolean | null;
  onCheckShortName: (shortName: string) => void;
  internalUsers: InternalUser[];
  jobTitleGroups: JobTitleGroup[];
}

// Step 3: Modalities
export interface ModalitiesStepProps extends StepProps {
  onToggleModality: (modality: ModalityType) => void;
}

// Step 4: Session Configuration
export interface SessionConfigStepProps extends StepProps {
  onAddWorkshop: () => void;
  onRemoveWorkshop: (index: number) => void;
  onUpdateWorkshop: (index: number, updates: Partial<FormWorkshopInput>) => void;
  // MED-1350: owns the auto target weeks (see CreateCohortPage index).
  onCheckinsChange: (next: CheckinSettingsForm) => void;
}

// Step 5: Dates
export interface DatesStepProps extends StepProps {
  sessionConflicts: Record<ModalityType, boolean[]>;
  onAddConstraint: () => void;
  onRemoveConstraint: (index: number) => void;
  onAddCoachHuddle: () => void;
  onRemoveCoachHuddle: (index: number) => void;
  onAddTimeSlot: () => void;
  onRemoveTimeSlot: (index: number) => void;
  onUpdateTimeSlot: (index: number, updates: Partial<FormTimeSlot>) => void;
  // MED-1350: the check-in target weeks are set on this step.
  onCheckinsChange: (next: CheckinSettingsForm) => void;
}

// Step 6: Review
export interface ReviewStepProps extends StepProps {
  selectedPartner: Partner | undefined;
  selectedProgram: Program | undefined;
  partnerContacts: PartnerContactWithCohortCount[];
  internalUsers: InternalUser[];
  jobTitleGroups: JobTitleGroup[];
}

// ========================
// HELPER TYPES
// ========================

// Wizard step definition
export interface WizardStep {
  label: string;
  description?: string;
}

// Code availability check result
export interface CodeCheckResult {
  available: boolean;
}
